// ─── Écran IVV : faits techniques (Core) ──────────────────────────
// Remonter un fait technique et consulter les siens. Disponible dans TOUTE app
// de la suite ; la remontée est marquée avec l'application d'origine.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';
import { useAuth } from '../auth/AuthContext';
import { useApp } from '../app/AppContext';
import PageHeader from '../ui/PageHeader';
import {
  creerFait, ecouterMesFaits, etatInfo, GRAVITES, TYPES_FT, moduleInfo, appInfo,
  sousModulesDe, sousModuleInfo,
} from './faitsTechniques';

export default function EcranFT() {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { user, estIVV } = useAuth();
  const app = useApp();

  const [faits, setFaits] = useState([]);
  const [ouvert, setOuvert] = useState(false);
  const [titre, setTitre]   = useState('');
  const [contexte, setCtx]  = useState('');
  const [gravite, setGrav]  = useState('mineur');
  const [type, setType]     = useState('defaut');
  const [modu, setModu]     = useState(null);   // module concerné (défaut : celui de l'app)
  const [sousM, setSousM]   = useState('');    // sous-module (fonctionnalité)
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState('');

  useEffect(() => {
    if (!user?.uid) { setFaits([]); return; }
    // Cloison : on ne voit que les remontées faites DEPUIS cette application.
    const unsub = ecouterMesFaits(user.uid, app.id, setFaits);
    return () => unsub();
  }, [user?.uid, app.id]);

  async function envoyer() {
    if (!titre.trim()) return;
    setBusy(true); setErr('');
    try {
      await creerFait({
        auteurUid: user.uid,
        auteurNom: user.prenom || user.email || '',
        titre, contexte, gravite, type,
        appVersion: app.version,
        application: app.id,                 // d'où l'on remonte
        module: modu || app.moduleDefaut,    // ce qui est concerné
        sousModule: sousM,                   // fonctionnalité précise
      });
      setTitre(''); setCtx(''); setGrav('mineur'); setType('defaut'); setModu(null); setSousM(''); setOuvert(false);
    } catch (e) { setErr(e?.message || String(e)); }
    setBusy(false);
  }

  const puce = (liste, val, set) => (
    <View style={s.puces}>
      {liste.map(o => (
        <TouchableOpacity key={o.id} style={[s.puce, val === o.id && s.puceOn]} onPress={() => set(o.id)}>
          <Text style={[s.puceTxt, val === o.id && s.puceTxtOn]}>{o.label}</Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  return (
    <View style={s.page}>
      <PageHeader
        titre={t('ft.titre')} iconeTitre="🐞"
        action={{ icone: ouvert ? '✕' : '＋', onPress: () => setOuvert(o => !o), label: t('ft.actionNouveau') }}
      />
      <ScrollView contentContainerStyle={s.corps}>
        <Text style={s.statut}>
          {estIVV ? `✅ ${t('valideur.compteValideur')}` : `ℹ️ ${t('valideur.compteStandard')}`}
        </Text>

        {ouvert && (
          <View style={s.form}>
            <Text style={s.lbl}>{t('ft.labelTitre')} *</Text>
            <TextInput style={s.input} value={titre} onChangeText={setTitre}
              placeholder={t('ft.phTitreEx')} placeholderTextColor={UI.faint} />
            <Text style={s.lbl}>{t('ft.labelContexte')}</Text>
            <TextInput style={[s.input, { minHeight: 70, textAlignVertical: 'top' }]} multiline
              value={contexte} onChangeText={setCtx}
              placeholder={t('ft.phContexte')} placeholderTextColor={UI.faint} />
            <Text style={s.lbl}>{t('ft.labelModule')}</Text>
            <View style={s.puces}>
              {app.modules.map(m => {
                const on = (modu || app.moduleDefaut) === m.id;
                return (
                  <TouchableOpacity key={m.id} style={[s.puce, on && s.puceOn]} onPress={() => { setModu(m.id); setSousM(''); }}>
                    <Text style={[s.puceTxt, on && s.puceTxtOn]}>{m.emoji} {m.label}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            {sousModulesDe(modu || app.moduleDefaut).length > 0 && (
              <>
                <Text style={s.lbl}>{t('ft.labelFonctionnalite')}</Text>
                <View style={s.puces}>
                  {sousModulesDe(modu || app.moduleDefaut).map(sm => (
                    <TouchableOpacity key={sm.id} style={[s.puce, sousM === sm.id && s.puceOn]}
                      onPress={() => setSousM(sousM === sm.id ? '' : sm.id)}>
                      <Text style={[s.puceTxt, sousM === sm.id && s.puceTxtOn]}>{sm.emoji} {sm.label}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </>
            )}
            <Text style={s.lbl}>{t('ft.labelGravite')}</Text>
            {puce(GRAVITES, gravite, setGrav)}
            <Text style={s.lbl}>{t('ft.labelType')}</Text>
            {puce(TYPES_FT, type, setType)}
            {!!err && <Text style={s.err}>{err}</Text>}
            <TouchableOpacity style={[s.envoyer, (!titre.trim() || busy) && { opacity: 0.5 }]}
              disabled={!titre.trim() || busy} onPress={envoyer}>
              <Text style={s.envoyerTxt}>{busy ? t('ft.envoiEnCours') : t('ft.envoyerRemontee')}</Text>
            </TouchableOpacity>
            <Text style={s.note}>{t('ft.depuisLabel')} {appInfo(app.id).emoji} {app.nom} · v{app.version}</Text>
          </View>
        )}

        <Text style={s.sect}>{t('ft.mesRemonteesSur', { app: app.nom })} {faits.length ? `(${faits.length})` : ''}</Text>
        {faits.length === 0 && (
          <Text style={s.vide}>{t('ft.aucuneRemontee', { app: app.nom })}</Text>
        )}
        {faits.map(f => {
          const e = etatInfo(f.statut);
          const a = moduleInfo(f.module);
          const ap = appInfo(f.application);
          return (
            <View key={f.id} style={s.ft}>
              <View style={s.ftHaut}>
                <Text style={s.ftNum}>#{f.numero}</Text>
                <View style={[s.etat, { backgroundColor: e.bg }]}>
                  <Text style={[s.etatTxt, { color: e.couleur }]}>{e.label}</Text>
                </View>
                <Text style={s.ftApp}>{a.emoji} {a.label}<Text style={s.ftSrc}>  ·  {ap.emoji}</Text></Text>
              </View>
              <Text style={s.ftTitre} numberOfLines={2}>{f.titre}</Text>
              {!!f.sousModule && <Text style={s.ftSm}>{sousModuleInfo(f.sousModule).emoji} {sousModuleInfo(f.sousModule).label}</Text>}
              {!!f.contexte && <Text style={s.ftCtx} numberOfLines={2}>{f.contexte}</Text>}
            </View>
          );
        })}
        <View style={{ height: 24 }} />
      </ScrollView>
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  page:   { flex: 1, backgroundColor: UI.bg },
  corps:  { padding: 16 },
  statut: { fontSize: 12.5, color: UI.muted, marginBottom: 14 },
  form:   { backgroundColor: UI.card, borderRadius: 14, borderWidth: 1, borderColor: UI.border, padding: 14, marginBottom: 18 },
  lbl:    { fontSize: 12, fontWeight: '700', color: UI.muted, marginBottom: 6, marginTop: 8 },
  input:  { backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10,
            fontSize: 14.5, color: UI.ink, borderWidth: 0.5, borderColor: UI.champBord },
  puces:  { flexDirection: 'row', flexWrap: 'wrap', gap: 7 },
  puce:   { borderWidth: 1, borderColor: UI.border, borderRadius: 9, paddingHorizontal: 11, paddingVertical: 6 },
  puceOn: { borderColor: UI.accent, backgroundColor: UI.accentTint },
  puceTxt:{ fontSize: 12.5, color: UI.muted, fontWeight: '600' },
  puceTxtOn: { color: UI.accent },
  envoyer:{ backgroundColor: UI.accent, borderRadius: 11, paddingVertical: 12, alignItems: 'center', marginTop: 16 },
  envoyerTxt: { color: '#fff', fontWeight: '700', fontSize: 14.5 },
  note:   { fontSize: 11, color: UI.faint, textAlign: 'center', marginTop: 8 },
  err:    { color: UI.danger, fontSize: 13, marginTop: 8 },
  sect:   { fontSize: 12, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.5,
            color: UI.faint, marginBottom: 10 },
  vide:   { color: UI.muted, fontSize: 13.5 },
  ft:     { backgroundColor: UI.card, borderRadius: 12, borderWidth: 1, borderColor: UI.border, padding: 13, marginBottom: 9 },
  ftHaut: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 5 },
  ftNum:  { fontSize: 12, fontWeight: '800', color: UI.faint },
  etat:   { borderRadius: 7, paddingHorizontal: 8, paddingVertical: 3 },
  etatTxt:{ fontSize: 11, fontWeight: '700' },
  ftApp:  { marginLeft: 'auto', fontSize: 11, color: UI.muted, fontWeight: '600' },
  ftSrc:  { color: UI.faint, fontWeight: '400' },
  ftSm:   { fontSize: 11, color: UI.accent, fontWeight: '600', marginTop: 2 },
  ftTitre:{ fontSize: 14.5, fontWeight: '700', color: UI.ink },
  ftCtx:  { fontSize: 12.5, color: UI.muted, marginTop: 3 },
});
